import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Security as SecurityIcon,
  Fingerprint as BiometricsIcon,
  VpnKey as AuthIcon,
  School as SchoolIcon,
  Lock as LockIcon,
  VerifiedUser as VerifiedIcon,
  Shield as ShieldIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const IdentityBiometricsCapability: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/dev-technology-contracts');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to Dev Technology Contracts"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            Identity & Biometrics Capability
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Identity Management, Biometric Systems, Access Control & Security Compliance
          </Typography>
        </Box>
      </Box>

      {/* Capability Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Capability Overview
          </Typography>
          <Typography variant="body1" paragraph>
            Dev Technology Group delivers comprehensive identity and biometrics solutions that secure government systems 
            and protect sensitive data across DHS agencies. Our expertise spans identity and access management (IAM), 
            biometric verification systems, student and exchange visitor tracking (SEVIS), multi-factor authentication, 
            and compliance with federal security standards. We support ICE's $63M biometrics contract, manage 1.5M+ 
            international student identities through SEVIS, and implement zero-trust security architectures that meet 
            FISMA, NIST, and DHS requirements.
          </Typography>
        </CardContent>
      </Card>

      {/* Identity Management */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AdminIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Identity & Access Management (IAM)
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AuthIcon sx={{ fontSize: 40, color: '#7B1FA2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                  Authentication & Authorization
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Secure identity verification and access control systems.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Single Sign-On (SSO):</strong> SAML, OAuth 2.0, OpenID Connect</Typography></li>
                <li><Typography variant="body2"><strong>Multi-Factor Authentication:</strong> SMS, email, authenticator apps, hardware tokens</Typography></li>
                <li><Typography variant="body2"><strong>Federated Identity:</strong> Cross-agency identity federation</Typography></li>
                <li><Typography variant="body2"><strong>Directory Services:</strong> Active Directory, LDAP integration</Typography></li>
                <li><Typography variant="body2"><strong>Password Management:</strong> Policy enforcement, self-service reset</Typography></li>
                <li><Typography variant="body2"><strong>Session Management:</strong> Timeout policies, concurrent session control</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LockIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Access Control & RBAC
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Role-based access control and permissions management.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Role-Based Access Control:</strong> Hierarchical role definitions</Typography></li>
                <li><Typography variant="body2"><strong>Attribute-Based Access:</strong> Dynamic policy-based access</Typography></li>
                <li><Typography variant="body2"><strong>Least Privilege:</strong> Minimal access rights enforcement</Typography></li>
                <li><Typography variant="body2"><strong>Segregation of Duties:</strong> Conflict prevention and detection</Typography></li>
                <li><Typography variant="body2"><strong>Access Reviews:</strong> Periodic certification and recertification</Typography></li>
                <li><Typography variant="body2"><strong>Privileged Access:</strong> Admin account management and monitoring</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <VerifiedIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Identity Lifecycle Management
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                End-to-end identity provisioning and deprovisioning.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>User Provisioning:</strong> Automated account creation and setup</Typography></li>
                <li><Typography variant="body2"><strong>Identity Verification:</strong> Background checks, credential validation</Typography></li>
                <li><Typography variant="body2"><strong>Onboarding/Offboarding:</strong> Streamlined employee lifecycle</Typography></li>
                <li><Typography variant="body2"><strong>Access Requests:</strong> Self-service portal and approval workflows</Typography></li>
                <li><Typography variant="body2"><strong>Identity Governance:</strong> Compliance and audit reporting</Typography></li>
                <li><Typography variant="body2"><strong>Deprovisioning:</strong> Automated account termination</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ShieldIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Zero Trust Architecture
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Never trust, always verify security model.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Continuous Verification:</strong> Real-time identity and device validation</Typography></li>
                <li><Typography variant="body2"><strong>Micro-Segmentation:</strong> Network isolation and containment</Typography></li>
                <li><Typography variant="body2"><strong>Least Privilege Access:</strong> Just-in-time, just-enough access</Typography></li>
                <li><Typography variant="body2"><strong>Device Trust:</strong> Endpoint security and compliance checks</Typography></li>
                <li><Typography variant="body2"><strong>Behavioral Analytics:</strong> Anomaly detection and risk scoring</Typography></li>
                <li><Typography variant="body2"><strong>Policy Enforcement:</strong> Dynamic access decisions</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Biometric Systems */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <BiometricsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Biometric Verification Systems
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BiometricsIcon sx={{ fontSize: 40, color: '#D32F2F', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                  Facial Recognition
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Advanced facial biometric identification and verification.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Face Detection:</strong> Real-time face localization and tracking</Typography></li>
                <li><Typography variant="body2"><strong>Face Matching:</strong> 1:1 verification and 1:N identification</Typography></li>
                <li><Typography variant="body2"><strong>Liveness Detection:</strong> Anti-spoofing and presentation attack detection</Typography></li>
                <li><Typography variant="body2"><strong>Age Estimation:</strong> Demographic analysis and verification</Typography></li>
                <li><Typography variant="body2"><strong>Quality Assessment:</strong> Image quality scoring and feedback</Typography></li>
                <li><Typography variant="body2"><strong>Multi-Pose Recognition:</strong> Various angles and lighting conditions</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BiometricsIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Fingerprint Matching
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                High-accuracy fingerprint biometric systems.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Fingerprint Capture:</strong> Optical, capacitive, ultrasonic sensors</Typography></li>
                <li><Typography variant="body2"><strong>Minutiae Extraction:</strong> Ridge endings and bifurcations</Typography></li>
                <li><Typography variant="body2"><strong>Template Matching:</strong> Fast and accurate comparison algorithms</Typography></li>
                <li><Typography variant="body2"><strong>Multi-Finger Enrollment:</strong> 10-print capture and storage</Typography></li>
                <li><Typography variant="body2"><strong>AFIS Integration:</strong> Automated Fingerprint Identification System</Typography></li>
                <li><Typography variant="body2"><strong>Mobile Biometrics:</strong> Field-deployable fingerprint devices</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BiometricsIcon sx={{ fontSize: 40, color: '#7B1FA2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                  Iris Recognition
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                High-security iris biometric identification.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Iris Capture:</strong> Near-infrared imaging technology</Typography></li>
                <li><Typography variant="body2"><strong>Pattern Analysis:</strong> Unique iris texture extraction</Typography></li>
                <li><Typography variant="body2"><strong>High Accuracy:</strong> Very low false acceptance rates</Typography></li>
                <li><Typography variant="body2"><strong>Non-Contact:</strong> Hygienic and user-friendly</Typography></li>
                <li><Typography variant="body2"><strong>Dual Iris:</strong> Both eyes for enhanced security</Typography></li>
                <li><Typography variant="body2"><strong>Distance Capture:</strong> On-the-move iris recognition</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BiometricsIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Multimodal Biometrics
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Combined biometric modalities for enhanced security.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Face + Fingerprint:</strong> Dual-factor biometric authentication</Typography></li>
                <li><Typography variant="body2"><strong>Iris + Face:</strong> High-security combination</Typography></li>
                <li><Typography variant="body2"><strong>Score Fusion:</strong> Combined confidence scoring</Typography></li>
                <li><Typography variant="body2"><strong>Fallback Options:</strong> Alternative modalities for accessibility</Typography></li>
                <li><Typography variant="body2"><strong>Enhanced Accuracy:</strong> Reduced false positives/negatives</Typography></li>
                <li><Typography variant="body2"><strong>Adaptive Systems:</strong> Context-based modality selection</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* SEVIS Support */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        SEVIS - Student & Exchange Visitor System
      </Typography>

      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            ICE SEVIS Support Contract - $64 Million
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                System Overview:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Tracks 1.5M+ international students and exchange visitors</Typography></li>
                <li><Typography variant="body2">Monitors 8,000+ certified schools and programs</Typography></li>
                <li><Typography variant="body2">Manages F, M, and J visa categories</Typography></li>
                <li><Typography variant="body2">Real-time status tracking and compliance monitoring</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Key Capabilities:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Student identity verification and biometric integration</Typography></li>
                <li><Typography variant="body2">School certification and compliance management</Typography></li>
                <li><Typography variant="body2">I-20 and DS-2019 form processing</Typography></li>
                <li><Typography variant="body2">Automated alerts and notifications</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Security Compliance */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Security Compliance & Standards
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#003366' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Standard/Framework</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Description</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Compliance Areas</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>FISMA</strong></TableCell>
              <TableCell>Federal Information Security Management Act</TableCell>
              <TableCell>Risk assessment, security controls, continuous monitoring</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>NIST 800-53</strong></TableCell>
              <TableCell>Security and Privacy Controls</TableCell>
              <TableCell>Access control, identification, authentication</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>NIST 800-63</strong></TableCell>
              <TableCell>Digital Identity Guidelines</TableCell>
              <TableCell>Identity proofing, authentication, federation</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>FedRAMP</strong></TableCell>
              <TableCell>Federal Risk and Authorization Management Program</TableCell>
              <TableCell>Cloud security, authorization, continuous monitoring</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>PIV/CAC</strong></TableCell>
              <TableCell>Personal Identity Verification</TableCell>
              <TableCell>Smart card authentication, PKI integration</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>DHS 4300A</strong></TableCell>
              <TableCell>DHS Sensitive Systems Policy</TableCell>
              <TableCell>System categorization, security requirements</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Government Applications */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Government Identity & Biometrics Applications
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Border Security & Immigration:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Traveler identity verification at ports of entry</Typography></li>
                <li><Typography variant="body2">Biometric visa and passport processing</Typography></li>
                <li><Typography variant="body2">SEVIS student and exchange visitor tracking</Typography></li>
                <li><Typography variant="body2">Watchlist screening and threat detection</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Internal Security & Access:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Employee and contractor identity management</Typography></li>
                <li><Typography variant="body2">Facility access control and physical security</Typography></li>
                <li><Typography variant="body2">Privileged user access management</Typography></li>
                <li><Typography variant="body2">Audit trails and compliance reporting</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Metrics */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <VerifiedIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Identity & Biometrics Performance Metrics
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                1.5M+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Student Identities
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                SEVIS tracking
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                99.9%+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Biometric Accuracy
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Match precision
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                24/7
              </Typography>
              <Typography variant="body2" color="text.secondary">
                System Availability
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Continuous operations
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                $63M
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ICE Biometrics Contract
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Active contract value
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default IdentityBiometricsCapability;
